import * as React from "react";
import { CalendarCheck, CalendarPlus } from "lucide-react";

import DatePicker from "react-datepicker";
import { useFormContext } from "react-hook-form";
import { ChronoEntryFormInputs } from "@/views/chronology/components/chronologyList/types";
import type { ChronoEntry } from "@/types/chronology";
import { startOfToday } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import clsx from "clsx";

interface Props {
  isEditable: boolean;
  chronoRecord?: ChronoEntry;
}

const CustomInput = (props: React.HTMLProps<HTMLInputElement>, _: React.Ref<HTMLInputElement>) => (
  <div {...props} className="flex items-center">
    {!props.value ? <CalendarPlus className="mr-2 h-4 w-4" /> : <CalendarCheck className="mr-2 h-4 w-4" />}
    {props.value ? props.value : <span className="text-xs">(no date)</span>}
  </div>
);

export const ChronologyListRecordDate: React.FC<Props> = ({ isEditable, chronoRecord }) => {
  const { setValue } = useFormContext<ChronoEntryFormInputs>();
  const [localDate, setLocalDate] = React.useState<Date | null>(null);
  const handleOnChange = React.useCallback(
    (date: Date | null) => {
      if (date) {
        setValue("day", date.getDate());
        setValue("month", date.getMonth() + 1);
        setValue("year", date.getFullYear());
        setLocalDate(date);
      }
    },
    [setValue, setLocalDate],
  );

  React.useEffect(() => {
    if (!chronoRecord) {
      handleOnChange(startOfToday());

      return;
    }

    if (chronoRecord.nlp_year == null || chronoRecord.nlp_month == null) {
      return;
    }

    handleOnChange(
      new Date(
        chronoRecord?.nlp_year,
        chronoRecord?.nlp_month ? chronoRecord?.nlp_month - 1 : 0,
        chronoRecord?.nlp_day || 1,
        10,
        0,
      ),
    );

    if (chronoRecord?.year && chronoRecord?.year !== -1) {
      handleOnChange(
        new Date(chronoRecord.year, chronoRecord.month ? chronoRecord.month - 1 : 0, chronoRecord.day || 1, 10, 0),
      );
    }
  }, [chronoRecord, handleOnChange]);

  return (
    <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm text-gray-500 sm:pl-6" valign="top">
      <div
        className={clsx({
          "cursor-pointer": isEditable,
        })}
      >
        <DatePicker
          dateFormat="YYYY-MM-dd"
          showMonthYearDropdown={true}
          selected={localDate}
          disabled={!isEditable}
          onChange={handleOnChange}
          customInput={React.createElement(React.forwardRef(CustomInput))}
          minDate={new Date(1900, 0, 1)}
          maxDate={new Date(2100, 11, 31)}
        />
      </div>
    </td>
  );
};
